import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';

import { Alert, Box, Grid, TextField, FormControlLabel, Radio, Card, Divider, Button, useTheme, useMediaQuery } from '@mui/material'
import Typography from '@mui/material/Typography'
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Heading from '../Heading/Heading'
import classes from './PaymentPageTemplate.module.css'
import CampusFeeBreakdown from '../CampusFeeBreakdown/CampusFeeBreakdown'
import CampusPaymentAmount from '../CampusFeeBreakdown/CampusPaymentAmount'
import FeeStructureAccordian from '../FeeStructureAccordian/FeeStructureAccordian'
import ParentDetails from '../ParentDetails/ParentDetails';
import { countFeeEligibleChildren } from '../../utils/parentHistoryHelpers';
import { currentYear, reenrolmentYear } from '../../utils/years';
import { childrenLabel, minimumFeeNote, payingTodayNote } from '../../utils/feeBreakdown';
import {
  CAMPUS_PAYMENT_MINIMUM,
  minimumFeeApplies,
  minimumFeeAppliesForChild,
  campusRowForChild,
  allowsCampusPaymentAmounts,
  campusPaymentKey,
  campusPaymentOptionCopy,
  campusPaymentSelectionsFromMap,
  campusPaymentSelectionsToMap,
  familyDiscountLabel,
  isMultiCampusPayment,
  usesPerCampusPaymentOptions,
} from '../../utils/campusPaymentOptions';

/**
 * Payment template — displays server-calculated fees only (no local fee math).
 *
 * Presentation follows the approved re-design (receipt card, credit banner,
 * sticky mobile action bar); every amount, flag and navigation payload still
 * comes from /calculatefees exactly as before.
 */
const PaymentPageTemplate = (props) => {
  const {
    feeCalculation,
    onRecalculate,
    DiscountOption: DiscountValue,
    SD: StaffDiscount,
    MC: MultipleCollegeChildren,
    FTE,
    credit_amount,
    campusPaymentSelections: incomingCampusPaymentSelections,
    earlyBirdAvailable = true,
    // The fee sandbox shows this page for a made-up family. Read-only, there is no way to pay: the
    // buttons are not drawn at all, and the two routes into payment refuse to run regardless.
    readOnly = false,
  } = props;

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const totals = feeCalculation?.totals || {};
  const payment = feeCalculation?.payment || {};
  const validation = feeCalculation?.validation || { valid: true, errorMessage: null };
  const students = feeCalculation?.students || [];
  const schoolfeearray = feeCalculation?.schoolFeeRows || [];
  const campusBreakdown = feeCalculation?.campusBreakdown || [];
  const totalCreditAvailable = payment.credit_amount ?? credit_amount ?? 0;

  const {
    TotalSchoolFees = 0,
    TotalDiscount = 0,
    TotalFeesWithDiscount = 0,
    TotalResourceFees = 0,
    TotalDepositFees = 0,
    TotalAdelaideResourceOnlyDiscount = 0,
    TotalOutstandingAmount = 0,
    PayableAmount = 0,
    MinimumAmount = 0,
    familyChildCount: serverFamilyChildCount = 0,
  } = totals;
  const familyChildCount = serverFamilyChildCount || countFeeEligibleChildren(props.enrolledchildrens);
  const depositAmounts = (students || [])
    .map((row) => Number(row.deposit_fee ?? row.original_deposit_fee))
    .filter((amount) => Number.isFinite(amount) && amount > 0);
  const uniqueDeposits = [...new Set(depositAmounts.map((amount) => amount.toFixed(2)))];
  const depositPerChild = uniqueDeposits.length === 1
    ? Number(uniqueDeposits[0])
    : (familyChildCount > 0 && Number(TotalDepositFees) > 0
      ? Number(TotalDepositFees) / familyChildCount
      : 200);

  const {
    showTextField = false,
    CustomAmount: serverCustomAmount = 0,
    AICCreditAmount = 0,
    BPointAmount = 0,
    BalanceDue = 0,
    payWithCard = false,
    payWithCreditOnly = false,
    AlreadyPaidAmount = 0,
    creditAlreadyUsed = 0,
    allCampusesPaid = false,
    partialPayment = false,
  } = payment;

  const isMultiCampus = isMultiCampusPayment(campusBreakdown, props.campusSessions, props.enrolledchildrens);
  // Credit belongs to a campus and can only be spent there, so how much to pay is decided
  // per campus — for one campus exactly as for three. The old family-level box is gone.
  const allowCustomAmount = false;
  const allowCampusAmounts = allowsCampusPaymentAmounts({
    staffDiscount: StaffDiscount,
    familyDiscountOption: DiscountValue,
  }) && !allCampusesPaid;
  const showCampusPaymentOptions = usesPerCampusPaymentOptions({
    isMultiCampus,
    staffDiscount: StaffDiscount,
    familyDiscountOption: DiscountValue,
  }) && !allCampusesPaid;
  const [CustomAmount, setCustomAmount] = useState(serverCustomAmount);
  const [RemAmountMethod, setRemAmountMethod] = useState('Not Applicable');
  const [campusPaymentSelectionMap, setCampusPaymentSelectionMap] = useState(() =>
    campusPaymentSelectionsToMap(incomingCampusPaymentSelections || campusBreakdown)
  );
  const isEditing = useRef(false);
  const recalcTimer = useRef(null);

  const showMinimum = minimumFeeApplies({
    allowCampusAmounts,
    allCampusesPaid,
    campusBreakdown,
    selections: campusPaymentSelectionMap,
  });
  // What is charged today: the server's credit-plus-card split, the same figures the card is
  // charged with.
  const payingToday = Number(AICCreditAmount || 0) + Number(BPointAmount || 0);
  // Campuses already paid: shown as settled, not folded into a total that reads like a bill.
  const paidCampusRows = campusBreakdown.filter((row) => row.paymentStatus === 'paid');
  const paidOnFor = (row) => {
    const status = (props.campusStatuses || []).find((campus) =>
      String(campus.SessionID) === String(row.SessionID)
      || String(campus.college || '').toLowerCase() === String(row.college || '').toLowerCase());
    return status?.paidOn || status?.t_date || '';
  };

  useEffect(() => {
    if (!isEditing.current) {
      setCustomAmount(serverCustomAmount);
    }
  }, [serverCustomAmount]);

  useEffect(() => {
    if (!campusBreakdown.length) return;
    setCampusPaymentSelectionMap((current) => {
      const next = { ...current };
      let changed = false;
      campusBreakdown.forEach((row) => {
        const key = campusPaymentKey(row);
        if (!key) return;
        const mode = row.paymentMode || current[key] || CAMPUS_PAYMENT_MINIMUM;
        if (next[key] !== mode && !current[key]) {
          next[key] = mode;
          changed = true;
        } else if (!next[key]) {
          next[key] = mode;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [campusBreakdown]);

  useEffect(() => {
    return () => {
      if (recalcTimer.current) clearTimeout(recalcTimer.current);
    };
  }, []);

  const handleRAMChange = (event) => {
    setRemAmountMethod(event.target.value);
  };

  const controlRAMProps = (item) => ({
    checked: RemAmountMethod === item,
    onChange: handleRAMChange,
    value: item,
    name: 'color-radio-button',
    inputProps: { 'aria-label': item },
  });

  const scheduleRecalc = (value) => {
    if (recalcTimer.current) clearTimeout(recalcTimer.current);
    recalcTimer.current = setTimeout(() => {
      if (onRecalculate) onRecalculate(value);
    }, 400);
  };

  const CustomAmountChange = (event) => {
    const customAmount = event.target.value;
    if (customAmount === '') {
      setCustomAmount('');
      return;
    }
    if (customAmount.match(/^\d*\.?\d{0,2}$/)) {
      setCustomAmount(customAmount);
      scheduleRecalc(customAmount);
    }
  };

  const giveHccDiscount = props.GiveHCCDiscount === true || props.GiveHCCDiscount === 'Yes';
  const discountContext = {
    giveHccDiscount,
    staffDiscount: StaffDiscount,
    familyDiscountOption: DiscountValue,
  };

  const campusPaymentSelectionList = campusPaymentSelectionsFromMap(
    campusPaymentSelectionMap,
    campusBreakdown,
    discountContext
  );

  const storedSelection = (campusKey) => {
    const entry = campusPaymentSelectionMap[campusKey];
    return typeof entry === 'string' ? { paymentMode: entry } : (entry || {});
  };

  /** What the box shows: what the parent typed, else what the server is charging this campus. */
  const campusAmountValue = (campus) => {
    const stored = storedSelection(campusPaymentKey(campus));
    if (stored.amount !== null && stored.amount !== undefined) return String(stored.amount);
    const serverAmount = Number(campus.amountToCover ?? campus.minimumAmount ?? 0);
    return serverAmount ? serverAmount.toFixed(2) : '';
  };

  const handleCampusAmountChange = (campusKey, value) => {
    // Money only, at most two decimals — same rule the old family box used.
    if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;

    const nextMap = {
      ...campusPaymentSelectionMap,
      [campusKey]: { ...storedSelection(campusKey), amount: value === '' ? null : value },
    };
    setCampusPaymentSelectionMap(nextMap);
    scheduleRecalc({
      campusPaymentSelections: campusPaymentSelectionsFromMap(nextMap, campusBreakdown, discountContext),
    });
  };

  const handleCampusPaymentModeChange = (campusKey, mode) => {
    const nextMap = { ...campusPaymentSelectionMap, [campusKey]: mode };
    setCampusPaymentSelectionMap(nextMap);
    const nextList = campusPaymentSelectionsFromMap(nextMap, campusBreakdown, discountContext);
    if (onRecalculate) onRecalculate({ campusPaymentSelections: nextList });
  };

  // Campuses still to be paid; a campus already settled needs no box.
  const payableCampuses = (campusBreakdown || []).filter((row) => !row.isPaid);

  const errorMessage = validation.errorMessage;

  // The campus split follows the box as the parent types; the server value takes
  // over once the amount is fixed (or when no box is shown).
  const enteredAmount = Number(CustomAmount);
  const amountToPay = allowCustomAmount && Number.isFinite(enteredAmount) && CustomAmount !== ''
    ? enteredAmount
    : serverCustomAmount;

  const buildNavState = () => ({
    SessionID: props.SessionID,
    campusSessions: props.campusSessions || [],
    parentID: props.parentID,
    college: props.college,
    famName: props.famName,
    childrens: props.enrolledchildrens,
    enrolledchildrens: props.enrolledchildrens,
    TotalResAmount: TotalResourceFees?.toFixed(2),
    TotalDepFees: TotalDepositFees?.toFixed(2),
    TotalSchoolFeeswodisc: TotalSchoolFees?.toFixed(2),
    TotalDiscount: TotalDiscount?.toFixed(2),
    TotalFeesWithDiscount: TotalFeesWithDiscount?.toFixed(2),
    PayableAmount: PayableAmount?.toFixed(2),
    CustomAmount: (BPointAmount + AICCreditAmount)?.toFixed(2),
    RemainingAmount: BalanceDue?.toFixed(2),
    AmountPaidFromAICCredit: Number(AICCreditAmount)?.toFixed(2),
    AmountPaidFromBpoint: BPointAmount?.toFixed(2),
    SelectedDiscountValue: DiscountValue,
    DiscountOption: DiscountValue,
    RemAmountMethod: RemAmountMethod,
    FTE: FTE,
    SD: StaffDiscount,
    SM: props.SM,
    MC: MultipleCollegeChildren,
    MultipleCollegeChildren: MultipleCollegeChildren,
    updatestud_detailsarr: props.updatestud_detailsarr,
    credit_amount: credit_amount,
    GiveHCCDiscount: props.GiveHCCDiscount,
    customAmount: allowCustomAmount ? CustomAmount : (serverCustomAmount || PayableAmount),
    campusPaymentSelections: campusPaymentSelectionList,
    earlyBirdAvailable,
  });

  const goToCardPayment = () => {
    if (readOnly) return;
    if (!props.isGhostLogin) {
      navigate('/bpointpaymentform', { state: buildNavState() });
    }
  };

  const goToCreditOnly = () => {
    if (readOnly) return;
    if (!props.isGhostLogin) {
      navigate('/thankyou2', {
        state: {
          ...buildNavState(),
          CustomAmount: Number(AICCreditAmount)?.toFixed(2),
        },
      });
    }
  };

  const actionButtonSx = (mobile) => ({
    bgcolor: '#059669',
    color: '#FFFFFF',
    fontWeight: 700,
    px: mobile ? 2 : 4,
    py: mobile ? 1.25 : 1.5,
    borderRadius: '12px',
    textTransform: 'none',
    fontSize: mobile ? '14px' : '16px',
    ...(mobile ? { minHeight: '48px', flexGrow: 1 } : {}),
    boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)',
    '&:hover': {
      bgcolor: '#047857',
      boxShadow: '0 6px 20px rgba(5, 150, 105, 0.45)',
    },
    '&.Mui-disabled': {
      bgcolor: '#94a3b8',
      color: '#f1f5f9',
      boxShadow: 'none',
    },
  });

  const actionsDisabled = !!errorMessage || !validation.valid;

  const creditButtonLabel = (mobile) => {
    if (partialPayment) {
      return mobile ? 'Complete from Credit Balance' : 'Complete remaining campus from credit';
    }
    return mobile ? 'Deduct from Credit Balance' : 'Deduct from Parent Credit Balance';
  };

  const summaryRow = (label, value, valueColor) => (
    <>
      <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Typography sx={{ color: '#475569', fontSize: '15px' }}>{label}</Typography>
        <Typography sx={{ color: valueColor || '#1e293b', fontWeight: 600, fontSize: '15px', whiteSpace: 'nowrap' }}>
          {value}
        </Typography>
      </Box>
      <Divider sx={{ mx: 3 }} />
    </>
  );

  const actions = (mobile) => (
    <>
      {payWithCard && (
        <Button
          variant="contained"
          size="large"
          endIcon={<NavigateNextIcon />}
          disabled={actionsDisabled}
          onClick={goToCardPayment}
          sx={actionButtonSx(mobile)}
        >
          {partialPayment ? 'Pay remaining amount' : 'Proceed to Payment'}
        </Button>
      )}

      {payWithCreditOnly && (
        <Button
          variant="contained"
          size="large"
          endIcon={<NavigateNextIcon />}
          disabled={actionsDisabled}
          onClick={goToCreditOnly}
          sx={actionButtonSx(mobile)}
        >
          {creditButtonLabel(mobile)}
        </Button>
      )}
    </>
  );

  const hasActions = !readOnly && (payWithCard || payWithCreditOnly);
  const chargedCampuses = campusBreakdown.filter((row) => Number(row.AmountPaidFromBpoint) > 0);
  const unpaidChargedCampuses = campusBreakdown.filter(
    (row) => row.paymentStatus !== 'paid' && Number(row.AmountPaidFromBpoint) > 0
  );

  return (
    <>
      <Heading text={'Fee Structure'} />
      {/*
        The children being re-enrolled, not every child the family has.
        
        This counted all of them, so a family with a child leaving after Year 12 was shown
        "2 Children" beside fees for one — the figures were right and the header disagreed with
        them, which reads as the fees being wrong rather than the label. On this screen "children"
        can only mean the ones being paid for.
      */}
      <ParentDetails
        ParentID={props.parentID}
        college={props.college}
        famName={props.famName}
        childrens={props.enrolledchildrens?.length ? props.enrolledchildrens : props.childrens}
        DiscountValue={familyDiscountLabel(campusBreakdown, DiscountValue)}
        FTE={FTE}
      />

      <Box>
        <FeeStructureAccordian
          SessionID={props.SessionID}
          SD={props.SD}
          HCCSelected={props.GiveHCCDiscount}
          schoolfeearray={schoolfeearray}
          items={(props.enrolledchildrens || [])
            .filter((c) => Number(c.year_grp) < 13)
            .map((c, i) => ({
              ...c,
              campus_name: c.campus_name || students[i]?.campus_name,
              campus_code: c.campus_code || c.cmpy_code || students[i]?.campus_code,
            }))}
          DiscountValue={DiscountValue}
          StudentDiscount={students}
          FTE={FTE}
          familyChildCount={familyChildCount}
          // Per child's campus, not the summary's one answer: one campus can be paid in full while
          // another is on the minimum.
          paidLabelFor={(child) => {
            const row = campusRowForChild(child, campusBreakdown);
            return row?.paymentStatus === 'paid' ? `Paid — ${row.college}${paidOnFor(row) ? `, ${paidOnFor(row)}` : ''}` : '';
          }}
          showMinimumFor={(child) => minimumFeeAppliesForChild({
            child,
            allowCampusAmounts,
            allCampusesPaid,
            campusBreakdown,
            selections: campusPaymentSelectionMap,
          })}
        />
      </Box>

      {/* Receipt card */}
      <Card sx={{
        mb: 4,
        mt: 3,
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
      }}>
        <Box sx={{ bgcolor: '#f8fafc', p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '18px' }}>
            {`${reenrolmentYear()} School Fees Breakdown for All Children`}
          </Typography>
        </Box>

        <Box sx={{ p: 0 }}>
          {paidCampusRows.length > 0 && (
            <Box sx={{ px: 3, py: 2, bgcolor: '#ecfdf5', borderBottom: '1px solid #a7f3d0' }} data-testid="already-paid">
              <Typography sx={{ color: '#065f46', fontWeight: 800, fontSize: '15px', mb: 0.5 }}>Already paid</Typography>
              {paidCampusRows.map((row) => (
                <Typography key={row.SessionID || row.college} sx={{ color: '#065f46', fontSize: '14px' }}>
                  {`${row.college} — paid${paidOnFor(row) ? ` ${paidOnFor(row)}` : ''}: $${Number(row.alreadyPaidAmount || 0).toFixed(2)}. Your children there are re-enrolled.`}
                </Typography>
              ))}
            </Box>
          )}
          {summaryRow(
            'Total School Fees (Without Discount)',
            DiscountValue === 'Multiple College' ? 'NA' : `$${TotalSchoolFees?.toFixed(2)}`
          )}

          {summaryRow(
            Number(TotalDiscount) > 0 && familyChildCount > 0
              ? `Total Discount Applied (${childrenLabel(familyChildCount)})`
              : 'Total Discount Applied',
            DiscountValue === 'Multiple College' ? 'NA' : `-$${Number(TotalDiscount).toFixed(2)}`,
            '#059669'
          )}

          {Number(TotalAdelaideResourceOnlyDiscount) > 0 &&
            summaryRow(
              'Adelaide 5th student onwards discount',
              `$${Number(TotalAdelaideResourceOnlyDiscount).toFixed(2)}`,
              '#7a4b00'
            )}

          {summaryRow(
            'Total School Fees (After Discount)',
            DiscountValue === 'Multiple College' ? 'NA' : `$${TotalFeesWithDiscount?.toFixed(2)}`
          )}

          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ color: '#475569', fontSize: '15px' }}>Total Resource Fees</Typography>
            <Typography sx={{ color: '#1e293b', fontWeight: 600, fontSize: '15px', whiteSpace: 'nowrap' }}>
              {`$${TotalResourceFees?.toFixed(2)}`}
            </Typography>
          </Box>

          {/*
            What the family still owes for the year now ending. Its own line, never folded into a
            fee: it is not one, no discount touches it, and a parent who cannot see it named will
            ring to ask why the total moved.
          */}
          {TotalOutstandingAmount > 0 && (
            <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <Box>
                <Typography sx={{ color: '#475569', fontSize: '15px' }}>
                  {`Outstanding ${currentYear()} Fees`}
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '12px' }}>
                  (Included below, and payable with this re-enrolment)
                </Typography>
              </Box>
              <Typography sx={{ color: '#b45309', fontWeight: 600, fontSize: '15px', whiteSpace: 'nowrap' }}>
                {`$${Number(TotalOutstandingAmount).toFixed(2)}`}
              </Typography>
            </Box>
          )}

          {/* Highlighted payable total */}
          <Box sx={{
            px: 3,
            py: 2.5,
            bgcolor: '#f1f5f9',
            borderTop: '1px solid #cbd5e1',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}>
            <Box>
              <Typography sx={{ color: '#334155', fontSize: '16px', fontWeight: 600 }}>
                {paidCampusRows.length ? 'Total Fees for the Year, All Campuses' : 'Total Fees for All Children'}
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '12px' }}>
                {paidCampusRows.length
                  ? '(Including Resource Fees, and what is already paid above — not what you pay today)'
                  : '(Including Resource Fees)'}
              </Typography>
            </Box>
            <Typography sx={{ color: '#0f172a', fontWeight: 700, fontSize: '18px', whiteSpace: 'nowrap' }}>
              {`$${PayableAmount?.toFixed(2)}`}
            </Typography>
          </Box>

          {/*
            What the credit covers, and what is actually left to pay.

            The total above is the fees, gross — and it was the last figure on the card, so a family
            with credit read it as what they owed and had no way to see the credit being spent. It
            only ever appeared in the multi-campus banner, so a family at one campus saw nothing at
            all. These come from the server's own split, the same numbers the card is charged with.
          */}
          {/* What has to be paid today to complete re-enrolment, and what makes it up — only while
              some campus is being charged less than its full amount. */}
          {showMinimum && (
          <Box sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}>
            <Box>
              <Typography sx={{ color: '#334155', fontSize: '15px', fontWeight: 600 }}>
                Minimum Re-enrolment Fee
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '12px' }}>
                {`(${minimumFeeNote(depositPerChild, TotalOutstandingAmount)})`}
              </Typography>
            </Box>
            <Typography sx={{ color: '#0f172a', fontWeight: 600, fontSize: '16px', whiteSpace: 'nowrap' }}>
              {`$${Number(MinimumAmount || 0).toFixed(2)}`}
            </Typography>
          </Box>
          )}

          {/*
            What the family pays today, always — the server's own split (credit plus card), which is
            exactly what is charged. The card used to end at the year's total, so a family paying the
            minimum read the full amount as what they were about to pay.
          */}
          <Box sx={{
            px: 3,
            py: 2.5,
            bgcolor: '#e8f5e9',
            borderTop: '1px solid #a5d6a7',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}>
            <Box>
              <Typography sx={{ color: '#1b5e20', fontSize: '16px', fontWeight: 700 }}>
                Paying today
              </Typography>
              <Typography sx={{ color: '#33691e', fontSize: '12px' }}>
                {payingTodayNote({ showMinimum, balanceDue: BalanceDue, allCampusesPaid })}
              </Typography>
            </Box>
            <Typography sx={{ color: '#1b5e20', fontWeight: 800, fontSize: '20px', whiteSpace: 'nowrap' }}>
              {`$${payingToday.toFixed(2)}`}
            </Typography>
          </Box>

          {(AICCreditAmount > 0 || totalCreditAvailable > 0) && (
            <>
              {summaryRow(
                'Credit applied from your account',
                `-$${Number(AICCreditAmount || 0).toFixed(2)}`,
                '#059669'
              )}

              <Box sx={{
                px: 3,
                py: 2.5,
                bgcolor: '#f1f5f9',
                borderTop: '1px solid #cbd5e1',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}>
                <Box>
                  <Typography sx={{ color: '#334155', fontSize: '16px', fontWeight: 600 }}>
                    To pay by card
                  </Typography>
                  <Typography sx={{ color: '#64748b', fontSize: '12px' }}>
                    {BPointAmount > 0
                      ? 'Paying today, after your credit has been applied'
                      : 'Your credit covers it — nothing to pay by card'}
                  </Typography>
                </Box>
                <Typography sx={{ color: '#0f172a', fontWeight: 700, fontSize: '18px', whiteSpace: 'nowrap' }}>
                  {`$${Number(BPointAmount || 0).toFixed(2)}`}
                </Typography>
              </Box>

              {totalCreditAvailable > AICCreditAmount && (
                <Box sx={{ px: 3, py: 1.5 }}>
                  <Typography sx={{ color: '#64748b', fontSize: '12.5px' }}>
                    {`$${Number(totalCreditAvailable - AICCreditAmount).toFixed(2)} of your credit remains, and stays at the campus holding it.`}
                  </Typography>
                </Box>
              )}
            </>
          )}

        </Box>
      </Card>

      {/* Campus credit balances — only for multi-campus families (credit never transfers between colleges) */}
      {isMultiCampus && (totalCreditAvailable > 0 || creditAlreadyUsed > 0 || campusBreakdown.length > 0) && (
        <Box sx={{
          p: 2,
          borderRadius: '12px',
          bgcolor: '#ecfdf5',
          border: '1px solid #6ee7b7',
          mb: 4,
        }}>
          <Typography sx={{ color: '#064e3b', fontSize: '14px' }}>
            Campus credit balances are applied separately and cannot be transferred between colleges.
            {totalCreditAvailable > 0 && (
              <>
                {' '}Total available credit:{' '}
                <strong>${Number(totalCreditAvailable)?.toFixed(2)} AUD</strong>
                . Credit is applied to fees at the same campus first, before any card charge.
              </>
            )}
            {/*
              Named rather than added to what is available, so a family coming back for a second
              child does not see the credit their first payment spent as though they still had it.
              Credit added since is live and appears in the figure above.
            */}
            {creditAlreadyUsed > 0 && (
              <>
                {' '}Credit already used on an earlier payment:{' '}
                <strong>${Number(creditAlreadyUsed).toFixed(2)} AUD</strong>.
              </>
            )}
          </Typography>

          {campusBreakdown.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {campusBreakdown.map((row) => {
                const status = row.paymentStatus;
                const isPaid = status === 'paid';
                const needsPay = status === 'failed' || (status !== 'paid' && Number(row.AmountPaidFromBpoint) > 0);
                const statusLabel = isPaid
                  ? 'Paid'
                  : status === 'failed'
                    ? 'Payment needed'
                    : needsPay
                      ? 'To pay'
                      : 'No card charge';
                const statusColor = isPaid ? '#065f46' : needsPay ? '#b91c1c' : '#334155';
                return (
                  <Typography
                    key={row.college}
                    sx={{ fontSize: '13px', color: statusColor, fontWeight: isPaid || needsPay ? 600 : 400 }}
                  >
                    {row.college}: {statusLabel}
                    {isPaid
                      ? ` — $${Number(row.alreadyPaidAmount || 0).toFixed(2)} already received`
                      : ''}
                    {isPaid && Number(row.creditAlreadyUsed || 0) > 0
                      ? ` (including $${Number(row.creditAlreadyUsed).toFixed(2)} from credit)`
                      : ''}
                    {!isPaid && Number(row.credit_available || 0) > 0
                      ? ` — available credit $${Number(row.credit_available || 0).toFixed(2)}`
                      : ''}
                    {!isPaid && row.credit_applied > 0
                      ? ` (applied $${Number(row.credit_applied).toFixed(2)})`
                      : ''}
                    {!isPaid && Number(row.AmountPaidFromBpoint) > 0
                      ? ` — card $${Number(row.AmountPaidFromBpoint).toFixed(2)}`
                      : ''}
                  </Typography>
                );
              })}

              {partialPayment && (
                <Typography sx={{ fontSize: '13px', mt: 1, fontWeight: 600, color: '#b91c1c' }}>
                  {`A previous campus payment succeeded. Please pay the remaining campus${unpaidChargedCampuses.length === 1 ? '' : 'es'} now. Already paid: $${Number(AlreadyPaidAmount || 0).toFixed(2)}.`}
                </Typography>
              )}

            </Box>
          )}
        </Box>
      )}

      {/* How much to pay, campus by campus. Credit is held by a campus and spent only there. */}
      {allowCampusAmounts && payableCampuses.length > 0 && (
        <Card sx={{
          mb: 4,
          border: '1px solid #bfdbfe',
          borderRadius: '12px',
          boxShadow: 'none',
          bgcolor: '#eff6ff',
        }}>
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography sx={{ fontWeight: 600, color: '#1e3a8a', fontSize: '16px' }}>
              How much would you like to pay today?
            </Typography>
            <Typography sx={{ color: '#2563eb', fontSize: '13px', mt: 0.5 }}>
              {payableCampuses.length > 1
                ? 'Each campus is paid separately. Credit held at one campus cannot be used at another.'
                : 'Your credit is used first; anything left over goes on your card.'}
            </Typography>
          </Box>

          <Box sx={{ bgcolor: '#ffffff', borderTop: '1px solid #bfdbfe' }}>
            {payableCampuses.map((campus, index) => {
              const key = campusPaymentKey(campus) || campus.college;
              return (
                <Box key={key}>
                  {index > 0 && <Divider />}
                  <CampusPaymentAmount
                    campus={campus}
                    value={campusAmountValue(campus)}
                    onChange={(value) => handleCampusAmountChange(key, value)}
                  />
                </Box>
              );
            })}
          </Box>

          {errorMessage && (
            <Box sx={{ m: 3, mt: 2, p: 1.5, bgcolor: '#fef2f2', borderLeft: '4px solid #ef4444', borderRadius: '0 4px 4px 0' }}>
              <Typography sx={{ color: '#b91c1c', fontSize: '13px', fontWeight: 500 }}>
                {errorMessage}
              </Typography>
            </Box>
          )}
        </Card>
      )}

      {isMultiCampus && (
        <CampusFeeBreakdown
          students={students}
          enrolledChildren={props.enrolledchildrens}
          campusBreakdown={campusBreakdown}
          fallbackCampus={props.college}
          amountToPay={allowCustomAmount ? amountToPay : undefined}
          showPaymentOptions={false}
          campusPaymentSelections={campusPaymentSelectionMap}
          onPaymentModeChange={handleCampusPaymentModeChange}
          paymentOptionCopy={campusPaymentOptionCopy({
            giveHccDiscount,
            familyDiscountOption: DiscountValue,
          })}
        />
      )}

      {/* Remaining amount to pay for the re-enrolment year */}
      {(BalanceDue > 0 && StaffDiscount !== 'Yes') && (
        <Card sx={{
          mb: 4,
          border: '1px solid #e2e8f0',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        }}>
          <Box sx={{
            bgcolor: '#f8fafc',
            p: 2.5,
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}>
            <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '16px' }}>
              {`Remaining Amount to Pay for ${reenrolmentYear()}`}
            </Typography>
            <Typography sx={{ fontWeight: 700, color: '#b91c1c', fontSize: '16px', whiteSpace: 'nowrap' }}>
              {`$${BalanceDue?.toFixed(2)}`}
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon fontSize="inherit" />}
              sx={{
                mb: 2.5,
                borderRadius: '8px',
                alignItems: 'flex-start',
                bgcolor: '#eff6ff',
                border: '1px solid #93c5fd',
                color: '#1e3a8a',
                '& .MuiAlert-icon': { color: '#2563eb', pt: 0.25 },
              }}
            >
              {`If you are selecting Centrelink, Direct Debit, or a Payment Schedule through Parent Lounge to complete the remaining school fees for ${reenrolmentYear()}, please contact the school’s Accounts Receivable team to set up the payment method. Selecting a payment method here does not mean that it will automatically be activated. Parents will still need to complete the required forms to set up the selected payment method.`}
            </Alert>
            <Typography className={classes.remamount} sx={{ color: '#475569', fontSize: '15px', mb: 2, fontWeight: 500 }}>
              How would you like to pay the remaining amount?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {['Centrelink', 'Direct Deposit', 'Payment Schedules', 'Not Applicable'].map((method) => (
                <FormControlLabel
                  key={method}
                  value={method}
                  control={<Radio
                    {...controlRAMProps(method)}
                    sx={{ color: '#64748b', '&.Mui-checked': { color: '#0ea5e9' } }}
                  />}
                  label={method}
                  sx={{
                    color: '#334155',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    m: 0,
                    p: 1,
                    '&:hover': { bgcolor: '#f8fafc' },
                    ...(RemAmountMethod === method && {
                      bgcolor: '#f0f9ff',
                      borderColor: '#bae6fd',
                      '&:hover': { bgcolor: '#f0f9ff' },
                    }),
                  }}
                />
              ))}
            </Box>
          </Box>
        </Card>
      )}

      {/* Desktop action area */}
      {!isMobile && hasActions && (
        <Grid mt={2} container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{
              pt: 2,
              borderTop: '1px solid #E2E8F0',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 2,
            }}>
              {actions(false)}
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Mobile sticky action bar */}
      {isMobile && hasActions && (
        <>
          {/* Spacer so the sticky bar never covers the last card */}
          <Box sx={{ height: '76px' }} />
          <Box sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            px: 2,
            py: 1.5,
            bgcolor: 'rgba(255, 255, 255, 0.94)',
            backdropFilter: 'blur(16px)',
            borderTop: '1px solid rgba(226, 232, 240, 0.9)',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
          }}>
            {actions(true)}
          </Box>
        </>
      )}

      {allCampusesPaid && (
        <Card sx={{
          mt: 2,
          mb: 4,
          p: 2.5,
          border: '1px solid #6ee7b7',
          borderRadius: '12px',
          boxShadow: 'none',
          bgcolor: '#ecfdf5',
        }}>
          <Typography sx={{ color: '#065f46', fontSize: '14px', fontWeight: 600 }}>
            All campus payments for this family have already been received. No further payment is required.
          </Typography>
        </Card>
      )}
    </>
  );
};

export default PaymentPageTemplate;
